import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useRequestUrl, useCourt } from "../context/contextHooks";
import { statesLong } from "../global/utils";
import { useForm } from 'react-hook-form';
import {feeFromName, getFeeTotal, getPaymentRequest, validateFeeInfo} from "./miscFeeFunctions";
import { useStyles } from '../styles/styles';
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from '@mui/material/useMediaQuery'
import API from "../http/API";
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from "@mui/material/Grid";
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Typography from "@mui/material/Typography";
import TextField from '@mui/material/TextField';
import { PatternFormat } from "react-number-format";
import Button from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Backdrop from '@mui/material/Backdrop';
import ReplayIcon from "@mui/icons-material/Replay";
import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import Drawer from '@mui/material/Drawer';
import AddFee from "./AddFee";
import FeeList from "./FeeList";
import SelectedFees from "./SelectedFees";
import CompletePayment from "../completePayment/CompletePayment";
import PaymentDetails from "../completePayment/PaymentDetails";
import PaymentsTwoToneIcon from "@mui/icons-material/PaymentsTwoTone";
import ManageWidget from "../widgetPay/ManageWidget";

const MiscFees = () => {
    const navigate = useNavigate();
    const [requestUrl,] = useRequestUrl();
    const { register, setValue, getValues } = useForm();
    const classes = useStyles;
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('xl'));
    const phone = useMediaQuery(theme.breakpoints.down('md'));
    const [court,] = useCourt();
    const [actionResponse, setActionResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [feeData, setFeeData] = useState(null);
    const [selectedFees, setSelectedFees] = useState([]);
    const [selectedFee, setSelectedFee] = useState(null);
    const [allFees, setAllFees] = useState(null);
    const [deliveryOptions, setDeliveryOptions] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [showSelected, setShowSelected] = useState(false);
    const [openSelectedClicked, setOpenSelectedClicked] = useState(false);
    const [feeTotal, setFeeTotal] = useState(0);
    const [processingFee, setProcessingFee] = useState(0);
    const [openFees, setOpenFees] = useState(false);
    const [feeResponse, setFeeResponse] = useState(null);
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [paymentResult, setPaymentResult] = useState(null);
    const [openReceipt, setOpenReceipt] = useState(false);


    const clearMessages = () => {
        setErrorMessage(null);
        setActionResponse(null);    }

    const getFees = () => {
        clearMessages();
        setLoading(true);
        const url = `${requestUrl}/miscFees/${court.courtId}`;
        API.getRetrieve(url, 'miscFees', null, setActionResponse);
    }

    const getProcessingFee = (orgAmount) => {
        clearMessages();
        setLoading(true);
        const query = {
            courtId: court.courtId,
            paymentCategory: 'Online',
            paymentType: 'Card',
            courtAmount: orgAmount
        }
        const url = `${requestUrl}/calculateFee`;
        API.postAction(url, JSON.stringify(query), 'getProcessingFee', null, setActionResponse);
    }

    const feeClicked = (e) => {
        const feeName = e.target.value;
        if (feeName) {
            setSelectedFee(feeFromName(allFees, feeName));
        } else {
            setSelectedFee(null);
        }
        setValue('feeSelect', '');
    }

    const addSelected = (fee) => {
        clearMessages();
        if (fee.amount > 0) {
            setSelectedFees([...selectedFees, fee]);
            setSelectedFee(null);
        } else {
            setErrorMessage('To add a fee, the total amount must be greater than zero');
        }
    }

    const removeSelected = (key) => {
        const fees = selectedFees.filter(f => f.key !== key);
        setSelectedFees(fees);
        if (fees && fees.length > 0) {
            const total = getFeeTotal(fees, selectedOption);
            setFeeTotal(total);
            getProcessingFee(total);
        } else {
            setFeeTotal(0);
            setFeeResponse({citepayFee: 0, courtAmount: 0, totalAmount: 0})
        }
    }

    const clearSelected = () => {
        selectedFees.forEach(fee => removeSelected(fee.key));
        setSelectedFees([]);
        setSelectedOption(null);
        setValue('optionSelect', '');
    }

    const optionClicked = (e) => {
        const feeName = e.target.value;
        const option = feeFromName(deliveryOptions, feeName);
        setSelectedOption(option);
        const total = getFeeTotal(selectedFees, option);
        setFeeTotal(total);
        getProcessingFee(total);
    }

    const somethingSelected = () => {
        return ((selectedFees && selectedFees.length > 0) || (selectedOption));
    }


    const payClicked = () => {
        setShowSelected(false);
        clearMessages();
        const form = getValues();
        const errors = validateFeeInfo(selectedFees, selectedOption, form, court);
        if (errors && errors.length > 0) {
            setErrorMessage(errors);
        } else {
            setPaymentRequest(getPaymentRequest(court.courtId, selectedFees, selectedOption, form, feeTotal, processingFee));
        }
    }

    const openSelected = () => {
        setOpenSelectedClicked(true);
    }

    const toggleSelected = (open) =>
        (event) => {
            if (
                event.type === 'keydown' &&
                (event.key === 'Tab' || event.key === 'Shift')
            ) {
                return;
            }
            setShowSelected(open);
        }

    useEffect(() => {
        document.title = `Miscellaneous Fees - Neumo Payments`;
    }, []);

    useEffect(() => {
        const courtName = court?.name || 'Court';
        document.title = `Miscellaneous Fees - ${courtName} - CitePayUSA`;
    }, [court]);

    useEffect(() => {
        if (loading && actionResponse) {
            if (!actionResponse.error) {
                if (actionResponse.requestType === 'miscFees') {
                    setFeeData(actionResponse.data);
                    if (actionResponse.data.miscFees) {
                        setAllFees(actionResponse.data.miscFees);
                    }
                    if (actionResponse.data.deliveryOptions) {
                        setDeliveryOptions(actionResponse.data.deliveryOptions);
                    }
                } else if (actionResponse.requestType === 'getProcessingFee') {
                    setFeeResponse(actionResponse);
                }
            }
            setLoading(false);
        }
    }, [actionResponse])

    useEffect(() => {
        if (!feeData) getFees();
    }, [feeData])

    useEffect(() => {
        if ((selectedFees && selectedFees.length > 0) || selectedOption) {
            setShowSelected(true);
            const feeTot = getFeeTotal(selectedFees, selectedOption);
            getProcessingFee(feeTot);
            setFeeTotal(feeTot);
        } else {
            setFeeResponse({citepayFee: 0, courtAmount: 0, totalAmount: 0})
            setFeeTotal(0);
            setProcessingFee(0);
            setShowSelected(false);
        }
        setOpenSelectedClicked(false);
    }, [selectedFees, selectedOption, openSelectedClicked])

    useEffect(() => {
        if (feeResponse) {
            setProcessingFee(feeResponse.citepayFee);
        }
    }, [feeResponse])

    useEffect(() => {
        if (paymentRequest) {
            setOpenDialog(true);
        }
    }, [paymentRequest])

    useEffect(() => {
        if (paymentResult) {
            setOpenReceipt(true);
        }
    }, [paymentResult])

    useEffect(() => {
        if (!openReceipt && paymentResult) {
            setSelectedFees([]);
            setSelectedOption(null);
            setSelectedFee(null);
            setFeeTotal(0);
            setProcessingFee(0);
            setPaymentRequest(null);
            setPaymentResult(null);
        }
    }, [openReceipt])

    return (
        <>
            { paymentRequest && !paymentResult &&
                <>
                    { court.neumoPayOnline ?
                        <ManageWidget
                            openDialog={openDialog}
                            setOpenDialog={setOpenDialog}
                            paymentRequest={paymentRequest}
                            setPaymentRequest={setPaymentRequest}
                            setPaymentResult={setPaymentResult}
                            court={court}
                            feeResponse={feeResponse}
                        />
                        :
                        <CompletePayment
                            openDialog={openDialog}
                            setOpenDialog={setOpenDialog}
                            paymentRequest={paymentRequest}
                            setPaymentRequest={setPaymentRequest}
                            setPaymentResult={setPaymentResult}
                            court={court}
                        />

                    }
                </>
            }
            { paymentResult &&
                <PaymentDetails
                    paymentRequest={paymentRequest}
                    paymentResult={paymentResult}
                    openReceipt={openReceipt}
                    setOpenReceipt={setOpenReceipt}
                    court={court}
                />
            }
            { openFees &&
                <FeeList openFees={openFees} setOpenFees={setOpenFees} fees={allFees} />
            }
            { errorMessage && phone &&
                <Alert severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                    <AlertTitle>Error!</AlertTitle>
                    {errorMessage}
                </Alert>
            }
            {phone && (
                <AddFee payableFee={selectedFee} addSelected={addSelected} allFees={allFees} feeClicked={feeClicked} />

            )}
            <Typography variant={smallScreen ? 'h6' : 'h5'} component="h2" color='primary' sx={{...classes.title, marginTop: '1rem'}}>
                Pay Non-Case Fees
            </Typography>
            <Grid
                container
                spacing={3}
                justifyContent="center"
            >
                <Backdrop open={loading} sx={classes.backdrop}>
                    <CircularProgress color='inherit' />
                </Backdrop>
                <Grid item xs={12} md={4} sx={classes.gridBottom}>
                    <Paper elevation={3} sx={classes.controlPanel}>
                        <Grid container spacing={1} alignItems='top'>
                            { deliveryOptions &&
                                <Grid item xs={12}>
                                    <FormControl
                                        component="fieldset"
                                        fullWidth
                                        sx={classes.formControl}
                                        {...register('optionSelect')}
                                    >
                                        <FormLabel component="legend" htmlFor='optionSelect'>
                                            <Typography variant="subtitle1" component='label' for='optionSelect' color='primary'>
                                                Select a Delivery Option
                                            </Typography>
                                        </FormLabel>
                                        <Select
                                            name='optionSelect'
                                            native
                                            variant='outlined'
                                            fullWidth
                                            sx={{height: '2rem'}}
                                            onChange={optionClicked}
                                            inputProps={{id: 'optionSelect'}}
                                        >
                                            <option key='-1' value=''>None</option>
                                            {deliveryOptions && deliveryOptions.map((option) => (
                                                <option key={option.name} value={option.name} style={{fontSize: '.9rem'}}>{option.name}</option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            }
                            <Grid item xs={12} md={6} >
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('payorName')}
                                >
                                    <FormLabel htmlFor='payorName' shrink={true} sx={classes.inputLabel}>Your Name *</FormLabel>
                                    <TextField
                                        id='payorName'
                                        name='payorName'
                                        variant='outlined'
                                        fullWidth
                                        inputProps={{sx: classes.textbox, "aria-required": "true"}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6} >
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('partyName')}
                                >
                                    <FormLabel htmlFor='partyName' shrink={true} sx={classes.inputLabel}>Party Name</FormLabel>
                                    <TextField
                                        id='partyName'
                                        name='partyName'
                                        variant='outlined'
                                        fullWidth
                                        inputProps={{sx: classes.textbox}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6} >
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('address')}
                                >
                                    <FormLabel htmlFor='address' shrink={true} sx={classes.inputLabel}>Billing Address *</FormLabel>
                                    <TextField
                                        id='address'
                                        name='address'
                                        variant='outlined'
                                        fullWidth
                                        inputProps={{sx: classes.textbox, "aria-required": "true"}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6} >
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('city')}
                                >
                                    <FormLabel htmlFor='city' shrink={true} sx={classes.inputLabel}>City *</FormLabel>
                                    <TextField
                                        id='city'
                                        name='city'
                                        variant='outlined'
                                        fullWidth
                                        inputProps={{sx: classes.textbox, "aria-required": "true"}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6} >
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('state')}
                                >
                                    <FormLabel required htmlFor='state' shrink={true} sx={classes.inputLabel}>State </FormLabel>
                                    <Select
                                        id='state'
                                        name='state'
                                        native
                                        variant='outlined'
                                        fullWidth
                                        sx={{height: '2rem'}}
                                        inputProps={{id: 'state', "aria-required": "true", "aria-label": "State" }}
                                    >
                                        <option key='None' value=''>Select your state</option>
                                        {statesLong.map((state) => (
                                            <option key={state.key} value={state.value} style={{fontSize: '.9rem'}}>{state.text}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6} >
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('zip')}
                                >
                                    <FormLabel htmlFor='zip' shrink={true} sx={classes.inputLabel} >Billing Zip *</FormLabel>
                                    <PatternFormat
                                        id='zip'
                                        name='zip'
                                        customInput={TextField}
                                        type='text'
                                        autoComplete='off'
                                        variant='outlined'
                                        format='#####'
                                        InputProps={{
                                            style: classes.numberbox
                                        }}
                                        inputProps={{
                                            "aria-required": "true"
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6} >
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('email')}
                                >
                                    <FormLabel htmlFor='email' shrink={true} sx={classes.inputLabel}>Email Address</FormLabel>
                                    <TextField
                                        id='email'
                                        name='email'
                                        variant='outlined'
                                        fullWidth
                                        inputProps={{sx: classes.textbox}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6} >
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('phone')}
                                >
                                    <FormLabel htmlFor='phone' shrink={true} sx={classes.inputLabel}>Phone Number</FormLabel>
                                    <PatternFormat
                                        id='phone'
                                        name='phone'
                                        customInput={TextField}
                                        type='text'
                                        autoComplete='off'
                                        variant='outlined'
                                        format="(###) ###-####"
                                        InputProps={{
                                            style: classes.numberbox
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant='body2' component='Grid'>
                                    * indicates a required field.  Either an email address or a phone number are also required.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                    <Box component='div' flexWrap='wrap-reverse' sx={{display: 'flex', flexDirection: 'row-reverse', margin: '1rem', marginBottom: '0'}}>
                        <Button
                            variant='contained'
                            color='primary'
                            startIcon={!smallScreen && <ListAltTwoToneIcon />}
                            sx={{...classes.squashedButton, whiteSpace: 'nowrap', fontSize: {xs: '.7rem', xl: '.85rem'}}}
                            onClick={() => setOpenFees(true)}
                            aria-haspopup="dialog"
                        >
                            List Fees
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={!smallScreen && <ReplayIcon/>}
                            sx={{...classes.squashedButton, whiteSpace: 'nowrap', fontSize: {xs: '.7rem', xl: '.85rem'}}}
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </Button>
                        { somethingSelected() && !showSelected &&
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={!smallScreen && <PlaylistAddCheckRoundedIcon />}
                                sx={{...classes.squashedButton, whiteSpace: 'nowrap', fontSize: {xs: '.7rem', xl: '.85rem'}}}
                                onClick={openSelected}
                            >
                                See Selected
                            </Button>
                        }
                    </Box>
                    { selectedFees && selectedFees.length > 0 && feeTotal > 0 &&
                        <Box component='div' flexWrap='wrap-reverse' sx={{display: 'flex', flexDirection: 'row-reverse'}}>
                            <Button
                                variant='contained'
                                fullWidth
                                color='primary'
                                startIcon={smallScreen ? null : <PaymentsTwoToneIcon/>}
                                sx={{...classes.squashedButton, whiteSpace: 'nowrap', fontSize: {xs: '.7rem', xl: '.85rem'}}}
                                onClick={() => payClicked()}
                            >
                                Pay Selected Fee(s)
                            </Button>
                        </Box>
                    }
                </Grid>
                <Grid item xs={12} md={8} style={{paddingTop: phone ? '0' : '1.5rem'}}>
                    <Drawer
                        anchor='right'
                        open={showSelected}
                        onClose={toggleSelected(false)}
                        PaperProps={{style: {width: smallScreen ? '70vw' : '50vw', margin: '1rem', padding: '1rem'}}}
                    >
                        <SelectedFees
                            fees={selectedFees}
                            selectedOption={selectedOption}
                            removeSelected={removeSelected}
                            clearSelected={clearSelected}
                            feeTotal={feeTotal}
                            processingFee={processingFee}
                            splitFees={court.onlineCheckFeeConfig && !court.noChex}
                            setShowSelected={setShowSelected}
                            payClicked={payClicked}
                        />
                    </Drawer>
                    { errorMessage && !phone &&
                        <Alert severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                            <AlertTitle>Error!</AlertTitle>
                            {errorMessage}
                        </Alert>
                    }
                    {!phone && (
                        <AddFee payableFee={selectedFee} addSelected={addSelected} allFees={allFees} feeClicked={feeClicked} />

                    )}
                </Grid>
            </Grid>
        </>
    );
}

export default MiscFees;
